import { useEffect, useState, useCallback } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Package, AlertCircle, ArrowDownToLine, ArrowUpFromLine, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "in", quantity: 0 });

  const load = useCallback(async () => {
    const [p, h] = await Promise.all([api.get("/products"), api.get("/inventory/history")]);
    setProducts(p.data);
    setHistory(h.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const adjust = async () => {
    if (!form.product_id || !form.quantity) return toast.error("Select product and quantity");
    try {
      await api.post("/inventory/adjust", form);
      toast.success("Stock adjusted");
      setOpen(false);
      setForm({ type: "in", quantity: 0 });
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const lowStock = products.filter((p) => (p.current_stock || 0) < (p.min_stock || 0));
  const totalValue = products.reduce((s, p) => s + (p.current_stock || 0) * (p.purchase_price || 0), 0);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Inventory</h1>
          <p className="text-sm text-muted-foreground mt-1">Stock-in / stock-out · damage · transfers</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="adjust-stock-btn"><Plus size={16} className="mr-1" /> Adjust Stock</Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Total Products</div><div className="font-heading font-bold text-2xl mt-1">{products.length}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Stock Value</div><div className="font-heading font-bold text-2xl mt-1">{inr(totalValue)}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Low Stock Items</div><div className="font-heading font-bold text-2xl mt-1 text-orange-600">{lowStock.length}</div></Card>
      </div>

      <Tabs defaultValue="stock">
        <TabsList>
          <TabsTrigger value="stock" data-testid="tab-stock">Stock</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Movement History</TabsTrigger>
          <TabsTrigger value="low" data-testid="tab-low-stock">Low Stock ({lowStock.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="stock" className="mt-4">
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Warehouse</TableHead>
                  <TableHead>Current Stock</TableHead>
                  <TableHead>Min Stock</TableHead>
                  <TableHead>Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => {
                  const low = (p.current_stock || 0) < (p.min_stock || 0);
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell>{p.sku || "—"}</TableCell>
                      <TableCell>{p.warehouse || "Main"}</TableCell>
                      <TableCell><Badge variant={low ? "destructive" : "secondary"}>{p.current_stock || 0} {p.unit || ""}</Badge></TableCell>
                      <TableCell>{p.min_stock || 0}</TableCell>
                      <TableCell>{inr((p.current_stock || 0) * (p.purchase_price || 0))}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((h) => {
                  const prod = products.find((p) => p.id === h.product_id);
                  return (
                    <TableRow key={h.id}>
                      <TableCell>{h.created_at?.slice(0, 10)}</TableCell>
                      <TableCell className="font-medium">{prod?.name || h.product_id}</TableCell>
                      <TableCell>
                        <Badge variant={h.type === "in" ? "default" : "destructive"}>
                          {h.type === "in" ? <ArrowDownToLine size={12} className="mr-1" /> : <ArrowUpFromLine size={12} className="mr-1" />}
                          {h.type}
                        </Badge>
                      </TableCell>
                      <TableCell>{h.quantity}</TableCell>
                      <TableCell className="text-muted-foreground">{h.notes || "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
        <TabsContent value="low" className="mt-4">
          <Card className="overflow-hidden">
            {lowStock.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">All stock levels healthy 🎉</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead><TableHead>Current</TableHead><TableHead>Min</TableHead><TableHead>Shortfall</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lowStock.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell><Badge variant="destructive">{p.current_stock || 0}</Badge></TableCell>
                      <TableCell>{p.min_stock || 0}</TableCell>
                      <TableCell className="text-orange-600 font-semibold">{(p.min_stock || 0) - (p.current_stock || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust Stock</DialogTitle></DialogHeader>
          <div className="grid gap-4 mt-2">
            <div>
              <Label>Product</Label>
              <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={form.product_id || ""} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="adjust-product">
                <option value="">— Select product —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} (Stock: {p.current_stock || 0})</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="adjust-type">
                  <option value="in">Stock In</option>
                  <option value="out">Stock Out</option>
                  <option value="damage">Damage</option>
                  <option value="transfer">Transfer</option>
                </select>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })} data-testid="adjust-quantity" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={adjust} data-testid="adjust-save-btn">Adjust</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
